import React, { useEffect, FunctionComponent } from 'react';

import { WarningButton } from '@components/library/button';
import { WorkspaceEvents } from '@components/workspace/WorkspaceEnums';
import { ResetResponseServer, WorkspaceUpdated } from '@components/workspace/WorkspaceTypes';
import { resetOptions, successOptions } from '@helpers/sweet_alert_options';
import { useFetch } from '@api/ApiHelper';
import { resetWorksheetOptions } from '@components/workspace/helpers';

interface ResetSmartsheetConnectionProps {
  orgApiKey: string;
}

const errorNotify = (): void => {
  Notify.create({
    body: bfTranslate('Please try again or contact support'),
    title: bfTranslate('Error resetting connection'),
    type: 'error'
  });
};

const thingToDelete = bfTranslate('This will reset the Smartsheet Workspace connection owner.');

export const ResetSmartsheetConnection: FunctionComponent<ResetSmartsheetConnectionProps> = ({ orgApiKey }) => {
  const resetWorksheetConnection = useFetch<ResetResponseServer>(resetWorksheetOptions(orgApiKey));

  const handleReset = (): void => {
    window.swal(resetOptions({ thingToDelete }), (confirm: boolean): void => {
      if (confirm) {
        resetWorksheetConnection.fetch();
      }
    });
  };

  useEffect(() => {
    if (resetWorksheetConnection.response) {
      if (resetWorksheetConnection.response.status === 'success') {
        window.swal(successOptions({ successCopy: bfTranslate('Smartsheet connection successfully reset.') }));
        // update connection button on workspace showpage
        const resetWorksheetConnectionEvent = new CustomEvent<WorkspaceUpdated>(WorkspaceEvents.WorkspaceConnectionReset, {
          detail: { connectionReset: true }
        });
        window.dispatchEvent(resetWorksheetConnectionEvent);
      } else {
        errorNotify();
      }
    }
  }, [resetWorksheetConnection.response]);

  useEffect(() => {
    if (resetWorksheetConnection.error) errorNotify();
  }, [resetWorksheetConnection.error]);

  return (
    <div className="reset-smartsheet-connection-container">
      <div className="advanced-tab-settings__row">
        <div className="half-column">
          <p>{bfTranslate('Reset Smartsheet connection owner')}</p>
          <p className="advanced-setting-description">
            {bfTranslate("If the Workspace has been connected to a Smartsheet Workspace, resetting this connection will remove that user's authorization and disable the connection. Another Workspace owner or admin must reconnect to the Smartsheet Workspace to re-enable the link to Smartsheet.")}
          </p>
        </div>
        <div className="half-column">
          <WarningButton
            className="reset-smartsheet-connection-button"
            onClick={handleReset}
          >
            {bfTranslate('Reset Smartsheet Connection')}
          </WarningButton>
        </div>
      </div>
    </div>
  );
};
